/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { Order, PagingRequest, QueryResult, ResourceQueryResult, Services } from '../util/services';
import { List, Map, Set } from 'immutable';
import { DownloadedFile } from '../util/downloaded-files';
import { ClientDocumentResource, ClientDocumentResourceService } from './client-document-resource.service';
import { OffsetDateTime } from '../util/dates';
import { DqlStoredQuery, DqlStoredQueryService } from '../dql/dql-stored-query.service';
import {CustomerRecord} from "../customer/customer-record.service";
import {CustomerRecordResource} from "../customer/customer-record-resource.service";
import {EmptyMessage} from "../util/messages";

/* eslint-enable */

@Injectable()
export class ClientDocumentService implements ClientDocument.Service, DqlStoredQuery.HolderService {

  private _dqlStoredQueryService: DqlStoredQueryService;

  constructor(private resourceService: ClientDocumentResourceService) {
    this._dqlStoredQueryService = new DqlStoredQueryService(resourceService.dqlStoredResourceService);
  }

  get dqlStoredQueryService(): DqlStoredQueryService {
    return this._dqlStoredQueryService;
  }

  query(request: ClientDocument.QueryRequest): Observable<QueryResult<ClientDocument.ClientDocument>> {
    return Observable.create((observer: Observer<QueryResult<ClientDocument.ClientDocument>>) => {
      const resourceRequest: ClientDocumentResource.QueryRequest = {
        ids: Services.createListParameter(request.ids),
        submitted_only: request.submittedOnly,
        offer_only: request.offerOnly,
        document_type: request.documentType,
        dql: request.dql,
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      };
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<ClientDocumentResource.ClientDocument>) => {
          observer.next({
            items: this.toPublicList(result.items),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  downloadPdf(request: ClientDocument.DownloadPdfRequest): Observable<DownloadedFile> {
    return this.resourceService.downloadPdf({
      id: request.id,
      document_type: request.documentType
    });
  }

  downloadZip(request: ClientDocument.QueryRequest): Observable<DownloadedFile> {
    const resourceRequest: ClientDocumentResource.QueryRequest = {
      ids: Services.createListParameter(request.ids),
      submitted_only: request.submittedOnly,
      document_type: request.documentType,
      dql: request.dql,
      order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
      page_number: request.paging ? request.paging.pageNumber : undefined,
      number_of_items: request.paging ? request.paging.numberOfItems : undefined,
    };
    return this.resourceService.downloadZip(resourceRequest);
  }

  downloadMergedZip(request: ClientDocument.QueryRequest): Observable<DownloadedFile> {
    const resourceRequest: ClientDocumentResource.QueryRequest = {
      ids: Services.createListParameter(request.ids),
      submitted_only: request.submittedOnly,
      document_type: request.documentType,
      dql: request.dql,
      order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
      page_number: request.paging ? request.paging.pageNumber : undefined,
      number_of_items: request.paging ? request.paging.numberOfItems : undefined,
    };
    return this.resourceService.downloadMergedZip(resourceRequest);
  }

  resendOfferEmail(request: ClientDocument.ResendRequest): Observable<EmptyMessage> {
    const resourceRequest: ClientDocumentResource.ResendRequest = {
      ids: request.ids.toArray(),
      document_type: request.documentType,
    };
    return this.resourceService.resendOfferEmail(resourceRequest);
  }


  private toPublicList(resourceList: ClientDocumentResource.ClientDocument[]): List<ClientDocument.ClientDocument> {
    return List.of(...resourceList.map((r) => ClientDocumentService.toPublic(r)));
  }

  static toPublic(r: ClientDocumentResource.ClientDocument): ClientDocument.ClientDocument {
    return {
      id: r.id,
      documentType: r.document_type,
      externalId: r.external_id,
      offerId: r.offer_id,
      state: <ClientDocument.State> r.state,
      customerRecordId: r.customer_record_id,
      taskRecordId: r.task_record_id,
      creatorUserId: r.creator_user_id,
      lastEditorUserId: r.last_editor_user_id,
      submitterUserId: r.submitter_user_id,
      offerUserId: r.offer_user_id,
      creationTime: Services.toOffsetDateTime(r.creation_time),
      updateTime: Services.toOffsetDateTime(r.update_time),
      submitTime: Services.toOffsetDateTime(r.submit_time),
      offerTime: Services.toOffsetDateTime(r.offer_time),
      pdfId: r.pdf_id,
      version: ClientDocumentService.toPublicElasticVersion(r.version),
      form: ClientDocumentService.toPublicColonnadeInsurance1ClientDocumentForm(r.form)
    };
  }

  private static toPublicElasticVersion(r?: ClientDocumentResource.ElasticVersion): ClientDocument.ElasticVersion | undefined {
    if (r) {
      return {
        seqNo: r.seq_no,
        primaryTerm: r.primary_term
      };
    }
    return undefined;
  }

  private static toPublicClientDocumentForm(r: ClientDocumentResource.ClientDocumentForm): ClientDocument.ClientDocumentForm {
    return {
      deletionCause: r.deletion_cause,
      deletionDate: Services.toOffsetDateTime(r.deletion_date),
      insuranceProduct: ClientDocumentService.toPublicClientDocumentFormInsuranceProductDictionary(r.insurance_product),
      insuranceType: ClientDocumentService.toPublicClientDocumentFormImageDictionary(r.insurance_type),
      insuranceLevel: ClientDocumentService.toPublicClientDocumentFormInsuranceLevel(r.insurance_level),
      paymentType: ClientDocumentService.toPublicClientDocumentFormImageDictionary(r.payment_type),
      sponsor: ClientDocumentService.toPublicClientDocumentFormImageDictionary(r.sponsor),
    };
  }

  static toPublicColonnadeInsurance1ClientDocumentForm(r: ClientDocumentResource.ColonnadeInsurance1ClientDocumentForm): ClientDocument.ColonnadeInsurance1ClientDocumentForm {
    return {
      ...ClientDocumentService.toPublicClientDocumentForm(r),
      branch: ClientDocumentService.toPublicClientDocumentFormImageDictionary(r.branch),
      contractingCustomerRecord: ClientDocumentService.toPublicCustomerRecord(r.contracting_customer_record),
      beneficiaryCustomerRecords: r.beneficiary_customer_records?.map((value) => ClientDocumentService.toPublicCustomerRecord(value)!),
      sponsorDiscount: ClientDocumentService.toPublicClientDocumentFormSponsorDiscount(r.sponsor_discount),
      vipDiscount: ClientDocumentService.toPublicClientDocumentFormVipDiscount(r.vip_discount),
      insuredCustomerRecord: ClientDocumentService.toPublicCustomerRecord(r.insured_customer_record),
      bankAccountNumber: r.bank_account_number,
      contactType: ClientDocumentService.toPublicClientDocumentFormImageDictionary(r.contact_type),
      personalInformationProvided: ClientDocumentService.toPublicObjectWithText(r.personal_information_provided),
      gatheringPersonalInformationApproved: ClientDocumentService.toPublicObjectWithText(r.gathering_personal_information_approved),
      marketingResearchApproved: ClientDocumentService.toPublicObjectWithText(r.marketing_research_approved),
      homeInsuranceInterested: ClientDocumentService.toPublicObjectWithText(r.home_insurance_interested),
      contractingSignature: r.contracting_signature,
      insurerSignature: r.insurer_signature
    };
  }

  private static toPublicClientDocumentFormInsuranceProductDictionary(r?: ClientDocumentResource.ClientDocumentFormInsuranceProductDictionary): ClientDocument.ClientDocumentFormInsuranceProductDictionary | undefined {
    if (r) {
      return {
        ...ClientDocumentService.toPublicClientDocumentFormImageDictionary(r),
        externalIdPrefix: r.external_id_prefix
      };
    }
    return undefined;
  }

  private static toPublicClientDocumentFormImageDictionary(r?: ClientDocumentResource.ClientDocumentFormImageDictionary): ClientDocument.ClientDocumentFormImageDictionary | undefined {
    if (r) {
      return {
        ...ClientDocumentService.toPublicObjectWithText(r),
        imageCode: r.image_code
      };
    }
    return undefined;
  }

  private static toPublicClientDocumentFormInsuranceLevel(r?: ClientDocumentResource.ClientDocumentFormInsuranceLevel): ClientDocument.ClientDocumentFormInsuranceLevel | undefined {
    if (r) {
      return {
        level: r.level,
        code: r.code,
        price: r.price,
        discountedPrice: r.discounted_price
      };
    }
    return undefined;
  }

  private static toPublicClientDocumentFormSponsorDiscount(r?: ClientDocumentResource.ClientDocumentFormSponsorDiscount): ClientDocument.ClientDocumentFormSponsorDiscount | undefined {
    if (r) {
      return {
        sponsorCode: r.sponsor_code,
        productCode: r.product_code,
        discountForint: r.discount_forint,
        discountPercent: r.discount_percent
      };
    }
    return undefined;
  }

  private static toPublicClientDocumentFormVipDiscount(r?: ClientDocumentResource.ClientDocumentFormVipDiscount): ClientDocument.ClientDocumentFormVipDiscount | undefined {
    if (r) {
      return {
        discountPercent: r.discount_percent
      };
    }
    return undefined;
  }

  private static toPublicCustomerRecord(r?: ClientDocumentResource.CustomerRecord): ClientDocument.CustomerRecord | undefined {
    if (r) {
      return {
        ...CustomerRecord.Mapper.toPublic(undefined, undefined, r)!,
        employeeId: r.employee_id,
        numberOfChildren: r.number_of_children,
        beneficiaryRate: r.beneficiary_rate
      };
    }
    return undefined;
  }

  private static toPublicObjectWithText(r?: ClientDocumentResource.ObjectWithText): ClientDocument.ObjectWithText | undefined {
    if (r) {
      return {
        text: r.text,
        code: r.code
      };
    }
    return undefined;
  }

}

export namespace ClientDocument {

  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<ClientDocument>>;

    // </editor-fold>

  }

  export interface ClientDocument {
    id: string,
    documentType: string,
    externalId: string,
    offerId?: string;
    state: State,
    form: ColonnadeInsurance1ClientDocumentForm,
    customerRecordId?: number,
    taskRecordId?: number,
    creatorUserId: number,
    lastEditorUserId: number,
    submitterUserId?: number,
    offerUserId?: number,
    creationTime: OffsetDateTime,
    updateTime: OffsetDateTime,
    submitTime: OffsetDateTime,
    offerTime: OffsetDateTime,
    pdfId?: number,
    version?: ElasticVersion,
  }

  export interface ClientDocumentForm {
    insuranceProduct?: ClientDocumentFormInsuranceProductDictionary;
    insuranceType?: ClientDocumentFormImageDictionary;
    paymentType?: ClientDocumentFormImageDictionary;
    insuranceLevel?: ClientDocumentFormInsuranceLevel;
    sponsor?: ClientDocumentFormImageDictionary;
    deletionDate: OffsetDateTime,
    deletionCause?: string
  }

  export interface ObjectWithText {
    text?: string;
    code?: string;
  }

  export interface ClientDocumentFormImageDictionary extends ObjectWithText {
    imageCode?: string;
  }

  export interface ClientDocumentFormInsuranceProductDictionary extends ClientDocumentFormImageDictionary {
    externalIdPrefix?: string;
  }

  export interface ClientDocumentFormInsuranceLevel {
    level?: number;
    code?: string;
    price?: number;
    discountedPrice?: number;
  }

  export interface ClientDocumentFormSponsorDiscount {
    sponsorCode?: string;
    productCode?: string;
    discountForint?: number;
    discountPercent?: number;
  }

  export interface ClientDocumentFormVipDiscount {
    discountPercent?: number;
  }

  export interface CustomerRecord extends CustomerRecord.CustomerRecord {
    employeeId?: string;
    numberOfChildren?: number;
    beneficiaryRate?: number;
  }

  export interface ColonnadeInsurance1ClientDocumentForm extends ClientDocumentForm {
    insuranceProduct?: ClientDocumentFormInsuranceProductDictionary;
    insuranceType?: ClientDocumentFormImageDictionary;
    paymentType?: ClientDocumentFormImageDictionary;
    insuranceLevel?: ClientDocumentFormInsuranceLevel;
    sponsor?: ClientDocumentFormImageDictionary;
    deletionDate: OffsetDateTime,
    deletionCause?: string

    // New properties
    branch?: ClientDocumentFormImageDictionary;
    contractingCustomerRecord?: CustomerRecord;
    beneficiaryCustomerRecords?: CustomerRecord[];
    sponsorDiscount?: ClientDocumentFormSponsorDiscount;
    vipDiscount?: ClientDocumentFormVipDiscount;
    insuredCustomerRecord?: CustomerRecord;
    bankAccountNumber?: string;
    contactType?: ClientDocumentFormImageDictionary;
    personalInformationProvided?: ObjectWithText;
    gatheringPersonalInformationApproved?: ObjectWithText;
    marketingResearchApproved?: ObjectWithText;
    homeInsuranceInterested?: ObjectWithText;
    contractingSignature?: string;
    insurerSignature?: string;
  }

  export interface ElasticVersion {
    seqNo: number,
    primaryTerm: number
  }

  export interface DownloadPdfRequest {
    id: string;
    documentType: string;
  }

  export interface QueryRequest {
    ids?: Set<string>;
    submittedOnly?: boolean;
    offerOnly?: boolean;
    documentType?: string;
    dql?: string;
    orders?: Set<Order<OrderField>>;
    paging?: PagingRequest;
  }

  export interface ResendRequest {
    ids: Set<string>;
    documentType: string;
  }

  export enum State {
    IN_PROGRESS = 'IN_PROGRESS',
    SUBMITTED = 'SUBMITTED',
    DELETED = 'DELETED',
    ACTIVE_OFFER =  'ACTIVE_OFFER',
    DELETED_OFFER = 'DELETED_OFFER'
  }

  export enum OrderField {
    EXTERNAL_ID,
    OFFER_ID,
    SUBMIT_TIME,
    EVENT_TIME,
    CREATION_TIME
  }

}

class Keys {

  private static readonly EXTERNAL_ID = 'external_id.keyword';
  private static readonly OFFER_ID = 'offer_id.keyword';
  private static readonly SUBMIT_TIME = 'submit_time';
  private static readonly EVENT_TIME = 'event_time';
  private static readonly CREATION_TIME = 'creation_time';

  private static readonly orderFieldKeyMap: Map<ClientDocument.OrderField, string> = Map.of(
    ClientDocument.OrderField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    ClientDocument.OrderField.OFFER_ID, Keys.OFFER_ID,
    ClientDocument.OrderField.SUBMIT_TIME, Keys.SUBMIT_TIME,
    ClientDocument.OrderField.EVENT_TIME, Keys.EVENT_TIME,
    ClientDocument.OrderField.CREATION_TIME, Keys.CREATION_TIME,
  );

  public static toOrderFieldKey(field: ClientDocument.OrderField): string {
    return Keys.orderFieldKeyMap.get(field)!;
  }

}
